(() => {
  'use strict';

  if (window.customElements.get('el-accessory-recommendations')) return;

  const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const machineMatches = (required, machine) => {
    const value = normalize(machine);
    if (required === 'luna-g3') return value.includes('lunag3');
    if (required === 'transcend-micro') return value.includes('transcendmicro');
    return false;
  };

  class ElAccessoryRecommendations extends HTMLElement {
    constructor() {
      super();
      this.busyForms = new WeakSet();
      this.onSubmit = this.onSubmit.bind(this);
      this.onPlanChange = this.applyMachineFit.bind(this);
    }

    connectedCallback() {
      if (this.dataset.elAccessoryReady === 'true') return;
      this.dataset.elAccessoryReady = 'true';
      try {
        this.strings = JSON.parse(this.querySelector('[data-el-accessory-strings]')?.textContent || '{}');
      } catch (_) {
        this.strings = {};
      }
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('el:power-plan:change', this.onPlanChange);
      window.addEventListener('pageshow', this.onPlanChange);
      document.addEventListener('DOMContentLoaded', this.onPlanChange, { once: true });
      this.applyMachineFit();
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('el:power-plan:change', this.onPlanChange);
      window.removeEventListener('pageshow', this.onPlanChange);
      document.removeEventListener('DOMContentLoaded', this.onPlanChange);
    }

    applyMachineFit() {
      const context = this.dataset.context || 'pdp';
      const machine = window.ELPowerPlan?.read?.().machine || '';
      const limit = Math.max(1, Math.min(4, Number(this.dataset.maxItems) || (context === 'pdp' ? 4 : 3)));
      const cards = [...this.querySelectorAll('[data-el-accessory-card]')];
      let visible = 0;
      cards.forEach(card => {
        const required = card.dataset.requiresMachine;
        const eligible = context === 'pdp' || !required || machineMatches(required, machine);
        const show = eligible && visible < limit;
        card.hidden = !show;
        if (show) visible += 1;
      });
      this.hidden = visible === 0;
      this.dataset.visibleItems = String(visible);
    }

    setStatus(form, message, role = 'status', showCartLink = false) {
      const status = form.querySelector('[data-el-accessory-status]');
      if (!status) return;
      status.setAttribute('role', role);
      const messageNode = status.querySelector('[data-el-accessory-status-message]');
      const cartLink = status.querySelector('[data-el-accessory-cart-link]');
      if (messageNode) messageNode.textContent = message;
      status.hidden = !message;
      if (cartLink) cartLink.hidden = !showCartLink;
    }

    sectionIdFor(form) {
      const section = form.closest('.shopify-section');
      const prefix = 'shopify-section-';
      return section?.id?.startsWith(prefix) ? section.id.slice(prefix.length) : '';
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-el-accessory-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      if (this.busyForms.has(form)) return;

      const button = form.querySelector('[data-el-accessory-add]');
      if (!button || button.disabled) return;
      this.busyForms.add(form);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = this.strings.adding || 'Adding…';
      this.setStatus(form, this.strings.adding || 'Adding…');

      let addConfirmed = false;
      let confirmedFailure = false;
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 15000);
      try {
        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections },
        }));
        const pageSectionId = this.dataset.context === 'cart-page' ? this.sectionIdFor(form) : '';
        if (pageSectionId) sections.push(pageSectionId);
        const uniqueSections = [...new Set(sections.filter(Boolean))];

        const formData = new FormData(form);
        formData.set('sections', uniqueSections.join(','));
        formData.set('sections_url', window.location.pathname);
        let response;
        try {
          response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: controller.signal,
          });
        } catch (error) {
          throw error;
        }

        confirmedFailure = response.status >= 400 && response.status < 500;
        if (response.ok) addConfirmed = true;
        let responseJson = {};
        try {
          responseJson = await response.json();
        } catch (_) {
          if (addConfirmed) throw new Error(this.strings.addedRefreshError || 'Item added; check your cart.');
        }
        if (!response.ok) throw new Error(responseJson.description || responseJson.message || this.strings.addError || 'Unable to add this item.');

        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!cartResponse.ok) throw new Error(this.strings.addedRefreshError || 'Item added; check your cart.');
        const cart = await cartResponse.json();
        cart.sections = responseJson.sections || {};

        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          const drawerSection = drawer.closest('.shopify-section');
          const drawerSectionId = drawerSection?.id?.replace(/^shopify-section-/, '') || '';
          const drawerHtml = drawerSectionId ? cart.sections[drawerSectionId] : null;
          const updatedDrawer = typeof drawerHtml === 'string'
            ? new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('.cart-drawer')
            : null;
          if (!updatedDrawer) throw new Error(this.strings.addedRefreshError || 'Item added; check your cart.');
        }

        if (pageSectionId) {
          const html = cart.sections[pageSectionId];
          if (typeof html !== 'string') throw new Error(this.strings.addedRefreshError || 'Item added; check your cart.');
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const currentSection = form.closest('.shopify-section');
          const updatedSection = documentFragment.getElementById(`shopify-section-${pageSectionId}`);
          if (!currentSection) throw new Error(this.strings.addedRefreshError || 'Item added; check your cart.');
          if (updatedSection) currentSection.replaceWith(updatedSection);
          else currentSection.innerHTML = documentFragment.body.innerHTML;
        }

        document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items || [responseJson],
            cart,
            blockCartDrawerOpening: this.dataset.context === 'cart-page',
          },
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart },
        }));

        if (button.isConnected) {
          button.textContent = this.strings.added || 'Added';
          this.setStatus(form, this.strings.added || 'Added');
        }
      } catch (error) {
        const message = addConfirmed
          ? (this.strings.addedRefreshError || 'This item was added, but the cart could not refresh. Check your cart before adding it again.')
          : confirmedFailure
            ? (error?.message || this.strings.addError || "We couldn't add this item. Check availability and try again.")
            : (this.strings.updateUnconfirmed || "We couldn't confirm the cart update. Check your cart before trying again.");
        this.setStatus(form, message, 'alert', true);
        if (button.isConnected) {
          button.textContent = addConfirmed
            ? (this.strings.added || 'Added')
            : confirmedFailure
              ? (this.strings.tryAgain || 'Try again')
              : (this.strings.add || 'Add');
          button.disabled = addConfirmed || !confirmedFailure;
        }
        if (addConfirmed) document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        window.clearTimeout(timer);
        if (button.isConnected) button.removeAttribute('aria-busy');
        this.busyForms.delete(form);
      }
    }
  }

  window.customElements.define('el-accessory-recommendations', ElAccessoryRecommendations);
})();
